import { supabase } from './supabaseClient';
import { JourneyStep, StepCompletion, CompletionMethod } from '../types/journey';

/**
 * Service for recording step completion
 * Most validation is now user-driven through UI checkboxes (Definition of Done)
 * This service only handles the final step completion recording
 */
export class StepCompletionValidationService {

  /**
   * DEPRECATED: validateStepCompletion - validation is now handled by UI checkboxes
   * Keeping this method for backward compatibility with existing code
   * but it no longer performs real-time database checks
   */
  static async validateStepCompletion(stepId: string): Promise<{
    isComplete: boolean;
    completedCriteria: string[];
    pendingCriteria: string[];
    automaticCompletion: boolean;
  }> {
    console.warn('⚠️ validateStepCompletion is deprecated - step completion is now controlled by UI checkboxes');
    
    // Return a default response indicating manual validation is required
    return {
      isComplete: false,
      completedCriteria: [],
      pendingCriteria: [],
      automaticCompletion: false
    };
  }

  /**
   * DEPRECATED: detectAutomaticCompletion - auto-detection removed
   * Completion is now explicitly controlled by user through Definition of Done checkboxes
   */
  static async detectAutomaticCompletion(stepId: string): Promise<{
    shouldAutoComplete: boolean;
    detectedActions: string[];
    metrics: Record<string, number>;
  }> {
    console.warn('⚠️ detectAutomaticCompletion is deprecated - completion is controlled by user');
    
    return {
      shouldAutoComplete: false,
      detectedActions: [],
      metrics: {}
    };
  }

  /**
   * Records step completion with validation
   * Requirements: 3, 7
   * This is the primary method - step completion is validated through UI checkboxes
   */
  static async recordStepCompletion(
    stepId: string,
    completionMethod: CompletionMethod,
    reflectionNotes?: string
  ): Promise<{
    completion: StepCompletion;
    pointsAwarded: number;
    validationResults: any;
  }> {
    try {
      // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('Authentication required');
      }

      // Get step details for points calculation
      const { data: stepRow, error: stepError } = await supabase
        .from('journey_steps')
        .select('*')
        .eq('id', stepId)
        .single();

      if (stepError || !stepRow) {
        throw new Error('Step not found');
      }

      // Calculate points awarded (removed - points are only deducted for AI features)
      let pointsAwarded = 0;
      // Points are no longer awarded for step completion - only deducted for AI features

      // Record completion
      const completionData = {
        step_id: stepId,
        user_id: user.id,
        completion_method: completionMethod,
        reflection_notes: reflectionNotes || null,
        points_awarded: pointsAwarded
      };

      const { data: completionRow, error: completionError } = await supabase
        .from('step_completions')
        .insert(completionData)
        .select()
        .single();

      if (completionError || !completionRow) {
        throw new Error(`Failed to record completion: ${completionError?.message || 'Unknown error'}`);
      }

      // Points are no longer awarded for step completion - only deducted for AI features

      const completion: StepCompletion = {
        id: completionRow.id,
        stepId: completionRow.step_id,
        userId: completionRow.user_id,
        completionMethod: completionRow.completion_method as CompletionMethod,
        reflectionNotes: completionRow.reflection_notes,
        pointsAwarded: completionRow.points_awarded,
        createdAt: new Date(completionRow.created_at)
      };

      return {
        completion,
        pointsAwarded,
        validationResults: null // No automatic validation - controlled by UI
      };

    } catch (error: any) {
      console.error('Error recording step completion:', error);
      throw new Error(error.message || 'Failed to record step completion');
    }
  }

  /**
   * Gets completion history for a step
   * Requirements: 8
   */
  static async getStepCompletionHistory(stepId: string): Promise<StepCompletion[]> {
    try {
      const { data: completionRows, error } = await supabase
        .from('step_completions')
        .select('*')
        .eq('step_id', stepId)
        .order('created_at', { ascending: false });

      if (error) {
        throw new Error(`Failed to get completion history: ${error.message}`);
      }

      return (completionRows || []).map(row => ({
        id: row.id,
        stepId: row.step_id,
        userId: row.user_id,
        completionMethod: row.completion_method as CompletionMethod,
        reflectionNotes: row.reflection_notes,
        pointsAwarded: row.points_awarded,
        createdAt: new Date(row.created_at)
      }));

    } catch (error: any) {
      console.error('Error getting completion history:', error);
      throw new Error(error.message || 'Failed to get completion history');
    }
  }


}